import { useState, useEffect, useCallback, useRef } from 'react';

const API = () => process.env.REACT_APP_API_URL;

/**
 * Fetch everything published by a reviewer (reviews + events).
 * Not limited by map viewport or date filters — the ผู้รีวิว tab shows the
 * reviewer's full body of work and lets the map fit to it.
 */
export const useReviewerItems = (reviewerId, language = 'TH', reviewerName = null) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!reviewerId) {
      setItems([]);
      setError(null);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    // Drop the previous reviewer's items immediately — otherwise the map would
    // briefly fit its region around the wrong person's locations.
    setItems([]);

    const url = `${API()}/reviewers/${reviewerId}/items`
      + `?language=${encodeURIComponent(language || 'TH')}`
      + (reviewerName ? `&name=${encodeURIComponent(reviewerName)}` : '');

    const run = async () => {
      try {
        console.log('👤 [useReviewerItems] GET', url);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status} — ${url}`);
        const data = await res.json();
        if (requestId !== requestIdRef.current) return; // stale response
        console.log('👤 [useReviewerItems] result:', data.counts, `total=${data.total}`);
        setItems(Array.isArray(data.items) ? data.items : []);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error('❌ [useReviewerItems] fetch failed:', err);
        setError(err.message || 'Failed to load reviewer items');
        setItems([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    };

    run();
  }, [reviewerId, language, reviewerName]);

  return { items, loading, error };
};

/**
 * Paged, stably-shuffled reviewer directory.
 *
 * The first page feeds the horizontal suggestion strip; the same accumulated
 * list backs the vertical list, which calls loadMore() when scrolled to the end.
 * `seed` keeps the shuffle consistent across pages within a session.
 */
export const useSuggestedReviewers = (pageSize = 20, autoLoad = true) => {
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const seedRef = useRef(Math.random().toString(36).slice(2, 10));
  const offsetRef = useRef(0);
  const inFlightRef = useRef(false);

  const fetchPage = useCallback(async (offset) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (offset === 0) setLoading(true); else setLoadingMore(true);

    const url = `${API()}/reviewers/suggested?limit=${pageSize}&offset=${offset}&seed=${seedRef.current}`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status} — ${url}`);
      const data = await res.json();
      const page = Array.isArray(data.reviewers) ? data.reviewers : [];

      console.log(`👥 [useSuggestedReviewers] offset=${offset} → ${page.length} (${data.source || 'n/a'}, more=${data.has_more})`);

      setReviewers((prev) => {
        if (offset === 0) return page;
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...page.filter((r) => !seen.has(r.id))];
      });
      setHasMore(!!data.has_more);
      offsetRef.current = offset + page.length;
    } catch (err) {
      console.warn('⚠️ [useSuggestedReviewers] fetch failed:', err.message);
      if (offset === 0) setReviewers([]);
      setHasMore(false);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, [pageSize]);

  const refresh = useCallback(() => {
    seedRef.current = Math.random().toString(36).slice(2, 10);
    offsetRef.current = 0;
    setHasMore(true);
    return fetchPage(0);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || inFlightRef.current) return;
    fetchPage(offsetRef.current);
  }, [hasMore, fetchPage]);

  useEffect(() => {
    if (autoLoad) fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  return { reviewers, loading, loadingMore, hasMore, refresh, loadMore };
};

/**
 * Reviewer name lookup for the magnifying-glass search box.
 */
export const useReviewerSearch = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [query, setQuery] = useState('');

  const search = useCallback(async (term) => {
    const q = String(term || '').trim();
    setQuery(q);

    if (!q) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const res = await fetch(`${API()}/reviewers/search?q=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResults(Array.isArray(data.reviewers) ? data.reviewers : []);
    } catch (err) {
      console.error('❌ [useReviewerSearch] failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResults([]);
    setHasSearched(false);
    setQuery('');
  }, []);

  return { results, loading, hasSearched, query, search, reset };
};

export default useReviewerItems;
